import { Elements } from '@/components/elements';
import clsx from 'clsx';
import Image from 'next/image';
import { Link } from 'react-router';
import {
    USE_CASES_EXPLORE_ITEMS,
    type UseCaseExploreSlug,
} from '@/utils/content/useCasesExploreItems';

export type { UseCaseExploreSlug } from '@/utils/content/useCasesExploreItems';

const EXPLORE_USE_CASE_IMAGE_WIDTH = 245;
const EXPLORE_USE_CASE_IMAGE_HEIGHT = 175;

export type ExploreOtherUseCasesProps = {
    /** Current page’s use case; the other four are shown. */
    excludeSlug: UseCaseExploreSlug;
    className?: string;
    title?: string;
    description?: string;
};

const DEFAULT_TITLE = 'Explore Other Use Cases';

const DEFAULT_DESCRIPTION =
    'Find how Predictable Benefits supports every role in the distribution ecosystem.';

export function ExploreOtherUseCases({
    excludeSlug,
    className,
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
}: ExploreOtherUseCasesProps) {
    const cards = USE_CASES_EXPLORE_ITEMS.filter(
        (item) => item.slug !== excludeSlug,
    );

    return (
        <section
            className={clsx(
                'overflow-hidden px-5 py-16 md:px-10 md:py-20 lg:px-[79px]',
                className,
            )}
        >
            <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-10">
                <div className="mx-auto flex max-w-[720px] flex-col gap-4 text-center">
                    <Elements.Title
                        title={title}
                        type="h2"
                        className="text-3xl font-semibold md:text-4xl"
                    />
                    <Elements.Description
                        description={description}
                        type="medium"
                        className="text-balance text-gray-700"
                    />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {cards.map((item) => (
                        <article
                            key={item.slug}
                            className="flex h-full flex-col gap-3 overflow-hidden rounded-2xl border border-gray-200/80 bg-white p-5 shadow-[0px_0px_5px_0px_#00000017,2px_2px_7px_0px_#0000000D]"
                        >
                            <div
                                className="relative w-full shrink-0 overflow-hidden rounded-lg bg-gray-100"
                                style={{
                                    height: EXPLORE_USE_CASE_IMAGE_HEIGHT,
                                }}
                            >
                                <Image
                                    src={item.image}
                                    alt={item.imageAlt}
                                    width={EXPLORE_USE_CASE_IMAGE_WIDTH}
                                    height={EXPLORE_USE_CASE_IMAGE_HEIGHT}
                                    className="h-full w-full object-cover"
                                    sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                                />
                            </div>
                            <div className="flex min-h-0 flex-1 flex-col gap-2">
                                <Elements.Title
                                    title={item.title}
                                    type="h3"
                                    className="text-left text-lg font-semibold text-gray-900 md:text-xl!"
                                />
                                <Elements.Description
                                    description={item.description}
                                    type="small"
                                    className="text-left text-gray-600"
                                />
                                <div className="mt-auto w-full pt-2">
                                    <Link
                                        to={item.href}
                                        className="block w-full"
                                    >
                                        <Elements.Button
                                            variant="primary"
                                            outline
                                            size="md"
                                            className="w-full"
                                            block
                                        >
                                            Learn More
                                        </Elements.Button>
                                    </Link>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
